import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import authService, { type StoreUser } from '../../../../services/authService';
import type { StoreDetailsTab } from '../../../../types/features/storeDetails/page';
import type { StoreDetailsInviteUserFormValues } from '../../../../types/features/storeDetails/users';
import { getCurrentUserDetails } from '../../../../utils/auth/currentUser';
import {
    storeDetailsInviteUserSchema,
    STORE_DETAILS_INVITE_USER_DEFAULT_VALUES,
} from '../validators/storeDetailsInviteUserSchema';
import { buildStoreDetailsInviteUserPayload } from '../utils/storeDetailsInviteUserUtils';

type UseStoreDetailsUsersParams = {
    isSuperAdmin: boolean;
    ownerUserId?: string | null;
    storeId?: string;
    activeTab: StoreDetailsTab;
};

export const useStoreDetailsUsers = ({ isSuperAdmin, ownerUserId, storeId, activeTab }: UseStoreDetailsUsersParams) => {
    const [storeUsers, setStoreUsers] = React.useState<StoreUser[]>([]);
    const [usersLoading, setUsersLoading] = React.useState(false);
    const [usersError, setUsersError] = React.useState('');
    const [addUserDialogOpen, setAddUserDialogOpen] = React.useState(false);
    const [inviteSubmitting, setInviteSubmitting] = React.useState(false);
    const [hiddenManagerSubmitting, setHiddenManagerSubmitting] = React.useState(false);
    const [mutatingStoreUserId, setMutatingStoreUserId] = React.useState<string | null>(null);
    const [removingStoreUserId, setRemovingStoreUserId] = React.useState<string | null>(null);
    const loadedStoreIdRef = React.useRef<string | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isValid },
    } = useForm<StoreDetailsInviteUserFormValues>({
        resolver: yupResolver(storeDetailsInviteUserSchema),
        mode: 'onChange',
        defaultValues: STORE_DETAILS_INVITE_USER_DEFAULT_VALUES,
    });

    const loadStoreUsers = React.useCallback(async (force = false) => {
        if (!storeId) return;
        if (!force && loadedStoreIdRef.current === storeId) {
            return;
        }
        try {
            setUsersLoading(true);
            setUsersError('');
            const users = await authService.getStoreUsers(storeId);
            setStoreUsers(users);
            loadedStoreIdRef.current = storeId;
        } catch (error) {
            console.error('Error loading store users:', error);
            setUsersError('Unable to load store users. Please try again.');
        } finally {
            setUsersLoading(false);
        }
    }, [storeId]);

    React.useEffect(() => {
        if (!storeId) {
            loadedStoreIdRef.current = null;
            setStoreUsers([]);
            return;
        }
        if (loadedStoreIdRef.current && loadedStoreIdRef.current !== storeId) {
            loadedStoreIdRef.current = null;
            setStoreUsers([]);
        }
    }, [storeId]);

    React.useEffect(() => {
        if (!isSuperAdmin || !storeId) return;
        void loadStoreUsers();
    }, [isSuperAdmin, loadStoreUsers, storeId]);

    const storeOwner = React.useMemo(() => {
        const owner = storeUsers.find((user) => {
            const role = (user.role || '').toLowerCase();
            return role === 'owner' || role === 'store_owner' || role.includes('owner');
        });
        return owner ?? null;
    }, [storeUsers]);

    const currentUserDetails = React.useMemo(() => getCurrentUserDetails(), []);

    const currentStoreUser = React.useMemo(() => {
        const currentUserId = currentUserDetails.id;
        const currentUserEmail = currentUserDetails.email_address?.toLowerCase();

        return storeUsers.find((user) => {
            return currentUserId
                ? user.user_id === currentUserId
                : Boolean(currentUserEmail && user.contact_email?.toLowerCase() === currentUserEmail);
        }) ?? null;
    }, [currentUserDetails.email_address, currentUserDetails.id, storeUsers]);

    const hiddenManagerActionLabel = currentStoreUser
        ? 'Remove me from store'
        : currentUserDetails.id && ownerUserId === currentUserDetails.id
            ? 'Restore me as store owner'
            : 'Add me as hidden manager';

    const hiddenManagerActionTitle = currentStoreUser
        ? 'Remove your account from this store'
        : currentUserDetails.id && ownerUserId === currentUserDetails.id
            ? 'Restore your owner access to this store'
            : 'Add your account as a hidden manager on this store';

    const handleHiddenManagerAction = React.useCallback(async () => {
        if (!storeId || hiddenManagerSubmitting) return;

        try {
            setHiddenManagerSubmitting(true);
            if (currentStoreUser) {
                if (ownerUserId && currentStoreUser.user_id === ownerUserId) {
                    toast.warning('Store owner access cannot be removed.');
                    return;
                }
                await authService.removeStoreUser(storeId, currentStoreUser.user_id);
                toast.success('Store access removed');
            } else {
                await authService.addSelfAsHiddenManager(storeId);
                toast.success('Hidden manager access added');
            }
            await loadStoreUsers(true);
        } catch (error) {
            console.error('Hidden manager action failed', error);
            toast.error('Unable to update hidden manager access. Please try again.');
        } finally {
            setHiddenManagerSubmitting(false);
        }
    }, [currentStoreUser, hiddenManagerSubmitting, loadStoreUsers, ownerUserId, storeId]);

    const handleToggleStoreUserVisibility = React.useCallback(async (user: StoreUser) => {
        if (!storeId || mutatingStoreUserId || removingStoreUserId) return;
        try {
            setMutatingStoreUserId(user.user_id);
            await authService.updateStoreUserVisibility(storeId, user.user_id, !user.is_hidden);
            await loadStoreUsers(true);
            toast.success(user.is_hidden ? 'User is now visible' : 'User is now hidden');
        } catch (error) {
            console.error('Visibility update failed', error);
            toast.error('Unable to update user visibility. Please try again.');
        } finally {
            setMutatingStoreUserId(null);
        }
    }, [loadStoreUsers, mutatingStoreUserId, removingStoreUserId, storeId]);

    const handleToggleDailyReconciliationEmail = React.useCallback(async (user: StoreUser) => {
        if (!storeId || mutatingStoreUserId || removingStoreUserId) return;
        try {
            const nextEnabled = !user.receive_daily_reconciliation_email;
            setMutatingStoreUserId(user.user_id);
            await authService.updateStoreUserDailyReconciliationEmail(storeId, user.user_id, nextEnabled);
            await loadStoreUsers(true);
            toast.success(nextEnabled ? 'Daily reconciliation email enabled' : 'Daily reconciliation email disabled');
        } catch (error) {
            console.error('Daily reconciliation email preference update failed', error);
            toast.error('Unable to update daily reconciliation email preference. Please try again.');
        } finally {
            setMutatingStoreUserId(null);
        }
    }, [loadStoreUsers, mutatingStoreUserId, removingStoreUserId, storeId]);

    const handleRemoveStoreUser = React.useCallback(async (user: StoreUser) => {
        if (!storeId || mutatingStoreUserId || removingStoreUserId) return;
        try {
            setRemovingStoreUserId(user.user_id);
            await authService.removeStoreUser(storeId, user.user_id);
            await loadStoreUsers(true);
            toast.success('User removed from store');
        } catch (error) {
            console.error('Remove user failed', error);
            toast.error('Unable to remove user. Please try again.');
        } finally {
            setRemovingStoreUserId(null);
        }
    }, [loadStoreUsers, mutatingStoreUserId, removingStoreUserId, storeId]);

    const handleOpenAddUserDialog = React.useCallback(() => {
        setAddUserDialogOpen(true);
    }, []);

    const handleCloseAddUserDialog = React.useCallback(() => {
        setAddUserDialogOpen(false);
        reset();
    }, [reset]);

    const handleInviteUser = handleSubmit(async (data) => {
        if (!storeId) return;
        try {
            setInviteSubmitting(true);
            await authService.inviteStoreUser(storeId, buildStoreDetailsInviteUserPayload(data));
            await loadStoreUsers(true);
            setAddUserDialogOpen(false);
            reset();
            toast.success('Invitation sent');
        } catch (error) {
            console.error('Invite failed', error);
            toast.error('Failed to send invite. Please try again.');
        } finally {
            setInviteSubmitting(false);
        }
    });

    return {
        addUserDialogOpen,
        control,
        setValue,
        handleCloseAddUserDialog,
        handleInviteUser,
        handleHiddenManagerAction,
        handleOpenAddUserDialog,
        handleRemoveStoreUser,
        handleToggleDailyReconciliationEmail,
        handleToggleStoreUserVisibility,
        hiddenManagerActionTitle,
        inviteIsValid: isValid,
        inviteSubmitting,
        currentStoreUser,
        hiddenManagerSubmitting,
        hiddenManagerActionLabel,
        mutatingStoreUserId,
        removingStoreUserId,
        storeOwner,
        storeUsers,
        usersError,
        usersLoading,
    };
};
